import React, { useState, useEffect, useCallback } from 'react';import { useNavigate, useLocation } from 'react-router-dom';
import { api, formatINR, formatISTDate } from '../lib/store';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { logger } from '../lib/logger';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Search, FileText, Download, ChevronLeft, ChevronRight, Eye, Trash2, Filter, X, GitMerge } from 'lucide-react';
import { toast } from 'sonner';
import { OrderStatusButtons } from '../components/cart/OrderStatusButtons';
import ReviewPossibleDuplicatesDialog from '../components/admin/ReviewPossibleDuplicatesDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

export default function QuotationsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  // Analytics drill-through can seed our filter via location.state
  // ({ salesperson_id, salesperson_name, start_date, end_date }). We read
  // this once on mount so a click from AnalyticsDashboard lands here
  // pre-filtered to the correct salesperson + date range.
  const seedState = location.state || {};
  const [quotations, setQuotations] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [quotationToDelete, setQuotationToDelete] = useState(null);

  // Date range filter. Default: last 10 days (inclusive today).
  // ``rangePreset`` drives which quick-chip appears active; picking a
  // preset sets startDate/endDate accordingly. Choosing "Custom" leaves
  // the dates as-is so the user can tweak them manually via the two
  // date inputs.
  const isoDaysAgo = (n) => {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return d.toISOString().slice(0, 10);
  };
  const isoToday = () => new Date().toISOString().slice(0, 10);
  const [startDate, setStartDate] = useState(seedState.start_date || (seedState.salesperson_id ? '' : isoDaysAgo(9)));
  const [endDate, setEndDate] = useState(seedState.end_date || (seedState.salesperson_id ? '' : isoToday()));
  const [rangePreset, setRangePreset] = useState(
    seedState.start_date ? 'custom' : (seedState.salesperson_id ? 'all' : '10d')
  );
  const applyPreset = (preset) => {
    setPage(1);
    setRangePreset(preset);
    if (preset === '10d')      { setStartDate(isoDaysAgo(9));  setEndDate(isoToday()); }
    else if (preset === '30d') { setStartDate(isoDaysAgo(29)); setEndDate(isoToday()); }
    else if (preset === 'month') {
      const now = new Date();
      setStartDate(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`);
      setEndDate(isoToday());
    }
    else if (preset === 'all') { setStartDate(''); setEndDate(''); }
    // 'custom' → leave dates untouched
  };

  // Admin-only "Review possible duplicates" dialog. Opened from the
  // toolbar; gives admins a one-screen way to walk through historical
  // dup pairs and either replace-old-with-new or confirm distinct.
  const [reviewDupsOpen, setReviewDupsOpen] = useState(false);
  // Salesperson filter — visible only to admin/manager/dispatcher who
  // are allowed to see other people's quotes. List populated from
  // /quotations/salespeople. Empty string = "All".
  const [salespeople, setSalespeople] = useState([]);
  const [salespersonId, setSalespersonId] = useState(seedState.salesperson_id || '');

  // ── Stale-only view state ─────────────────────────────────────────
  // When user clicks a "Pending Nd" badge on a card we switch to a
  // filtered view (range = all time, only pending 30+d cards). A
  // dismissible banner offers to restore the previous view. Prev
  // snapshot lets us revert exactly what the user had before
  // clicking, including any custom date range they'd picked.
  const [staleMode, setStaleMode] = useState(false);
  const [staleSnapshot, setStaleSnapshot] = useState(null);
  const enterStaleView = () => {
    if (staleMode) return;
    setStaleSnapshot({
      startDate, endDate, rangePreset, page,
    });
    setStaleMode(true);
    setRangePreset('all');
    setStartDate('');
    setEndDate('');
    setPage(1);
  };
  const exitStaleView = () => {
    if (!staleMode) return;
    if (staleSnapshot) {
      setStartDate(staleSnapshot.startDate);
      setEndDate(staleSnapshot.endDate);
      setRangePreset(staleSnapshot.rangePreset);
      setPage(staleSnapshot.page || 1);
    }
    setStaleMode(false);
    setStaleSnapshot(null);
  };

  // Deep-link entry: /quotations?stale=1 opens the stale-only view
  // directly on mount. Used by the 10 AM WhatsApp digest so tapping
  // the "⚠️ You have N pending quotations" line lands the user on the
  // pre-filtered list (after the login redirect completes). We keep
  // the effect to a single fire — `enterStaleView` is idempotent and
  // no-ops when we're already in stale mode.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('stale') === '1') {
      enterStaleView();
      // Strip the query param so a browser refresh doesn't perpetually
      // re-enter stale mode after the user has switched preset.
      const next = { ...location, search: '' };
      navigate(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helper — days between created_at and now. Used by the badge to
  // decide colour (amber 30-59, red 60+) and by the stale-only filter
  // when it hides non-stale rows. Uses IST-neutral epoch math since
  // the exact hour of day doesn't matter at day-granularity.
  const daysPending = (isoStr) => {
    if (!isoStr) return 0;
    const then = new Date(isoStr);
    if (isNaN(then)) return 0;
    return Math.floor((Date.now() - then.getTime()) / 86400000);
  };

  // Displayed rows — when in stale-only view we hide anything that
  // isn't pending 30+d. Search + salesperson filter still apply
  // because they're already baked into `quotations` server-side.
  const displayedQuotations = staleMode
    ? quotations.filter((q) => (q.order_status || 'pending').toLowerCase() === 'pending' && daysPending(q.created_at) >= 30)
    : quotations;
  const currentRole = JSON.parse(localStorage.getItem('user') || '{}').role || '';
  const isPrivileged = ['admin', 'manager', 'dispatcher'].includes(currentRole);

  // ── Stale-pending summary (net-count across ALL history) ─────────
  // The individual "Pending Nd" badges only appear on rows that are
  // actually loaded — so on the default 10-day view they'd never show
  // even if the DB is full of 30+ day-old pending quotes. This
  // separate endpoint gives us a stable "you have N stale" counter
  // we can surface as a banner at the top of the page, making the
  // feature discoverable no matter which date range is active.
  const [staleSummary, setStaleSummary] = useState({ count: 0, oldest_days: 0 });
  useEffect(() => {
    api.getStalePendingSummary()
      .then(r => setStaleSummary({
        count: r.data?.count || 0,
        oldest_days: r.data?.oldest_days || 0,
      }))
      .catch((err) => logger.warn('Failed to load stale summary:', err));
  }, []);

  // Load salespeople list once, only for users who can use the filter.
  useEffect(() => {
    if (!isPrivileged) return;
    api.getSalespeople()
      .then(r => setSalespeople(r.data?.salespeople || []))
      .catch((err) => logger.warn('Failed to load salespeople:', err));
  }, [isPrivileged]);

  const fetchQuotations = useCallback(() => {
    setLoading(true);
    // When a search string is present the query intentionally omits the
    // date-range parameters so the customer name can match ANYWHERE in
    // history (mirrors the "All time" chip's call shape). Clearing the
    // search box re-applies the currently-selected preset immediately.
    const searching = !!(search && search.trim());
    // Stale-only view (opened via badge or top-banner "View them") wants
    // ONLY quotes older than 30 days. We push a synthetic end_date at
    // T-30 so the backend returns just the aged rows and paging over
    // them actually works — otherwise the DESC sort would bury them
    // behind the newest 20 quotes and displayedQuotations would be
    // empty. Search still wins if the user types into the search box.
    let effectiveStart = searching ? '' : startDate;
    let effectiveEnd   = searching ? '' : endDate;
    if (staleMode && !searching) {
      effectiveStart = '';
      effectiveEnd = isoDaysAgo(30);
    }
    api.getQuotations({
      page, limit: 20, search, salesperson_id: salespersonId,
      start_date: effectiveStart,
      end_date:   effectiveEnd,
    })
      .then(r => {
        setQuotations(r.data.quotations);
        setTotal(r.data.total);
        setPages(r.data.pages);
      })
      .catch(logger.error)
      .finally(() => setLoading(false));
  }, [page, search, salespersonId, startDate, endDate, staleMode]);

  useEffect(() => {
    fetchQuotations();
  }, [fetchQuotations]);

  // Duplicate-map: `{duplicateQuotationId: keptQuotationId}` for every row
  // the dedupe service is currently collapsing. We fetch once on mount
  // (and refresh after unmerge) so each card can show a "Merged with #..."
  // badge. The map keys point AT duplicates; the values are the surviving
  // (latest) quotation each one is folded into.
  const [duplicateMap, setDuplicateMap] = useState({});
  const fetchDuplicateMap = () => {
    api.getQuotationDuplicateMap()
      .then(r => setDuplicateMap(r.data?.duplicates || {}))
      .catch((err) => { logger.warn('Failed to load duplicate-map:', err); setDuplicateMap({}); });
  };
  useEffect(() => { fetchDuplicateMap(); }, []);

  // Build the reverse map too — for a "winner" we want to show how many
  // older siblings it absorbed (e.g. "+2 merged").
  const winnerToDupCount = React.useMemo(() => {
    const counts = {};
    Object.values(duplicateMap).forEach((kept) => {
      counts[kept] = (counts[kept] || 0) + 1;
    });
    return counts;
  }, [duplicateMap]);

  const handleUnmerge = async (q) => {
    if (!window.confirm(`Mark quotation ${q.quotation_number} as a separate entry?\n\nIt will count individually in totals from now on.`)) return;
    try {
      await api.unmergeQuotation(q.id);
      toast.success('Quotation will now be counted separately');
      fetchDuplicateMap();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unmerge quotation');
    }
  };

  const handleDownload = async (q) => {
    try {
      const res = await api.downloadPdf(q.id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${q.quotation_number}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to download PDF');
    }
  };

  const formatDate = (iso) => formatISTDate(iso);

  const handleDeleteClick = (q) => {
    setQuotationToDelete(q);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!quotationToDelete) return;
    try {
      await api.deleteQuotation(quotationToDelete.id);
      toast.success(`Quotation ${quotationToDelete.quotation_number} deleted`);
      setDeleteDialogOpen(false);
      setQuotationToDelete(null);
      fetchQuotations();
    } catch {
      toast.error('Failed to delete quotation');
    }
  };

  return (
    <div className="max-w-[1600px] mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6" data-testid="quotations-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-6">
        <div>
          <h1 className="text-lg sm:text-xl font-bold text-[#1A1C16]">Quotation History</h1>
          <p className="text-xs sm:text-sm text-[#5C6150]">{total} quotations</p>
        </div>
        <div className="flex gap-2 flex-wrap justify-end w-full sm:w-auto">
          {(currentRole === 'admin' || currentRole === 'manager') && (
            <Button
              variant="outline"
              onClick={() => setReviewDupsOpen(true)}
              className="border-amber-600 text-amber-700 hover:bg-amber-50 text-xs uppercase tracking-wider h-9 sm:h-10 flex-1 sm:flex-none min-w-0"
              data-testid="review-duplicates-btn"
            >
              <GitMerge className="w-4 h-4 mr-1 sm:mr-2 flex-shrink-0" />
              <span className="truncate">Review duplicates</span>
            </Button>
          )}
          <Button onClick={() => navigate('/products')} className="bg-[#556B2F] hover:bg-[#465926] text-white text-xs uppercase tracking-wider h-9 sm:h-10 flex-1 sm:flex-none min-w-0" data-testid="create-new-quotation-btn">
            <FileText className="w-4 h-4 mr-1 sm:mr-2 flex-shrink-0" />
            <span className="truncate">New Quotation</span>
          </Button>
        </div>
      </div>

      {/* Sticky filter block — search + salesperson + date-range chips
          pin below the app navbar while the list scrolls beneath them.
          top-[60px] matches the navbar's h-[60px]; z-40 sits UNDER the
          navbar's z-50 so the header still overlaps this on scroll. */}
      <div className="sticky top-[60px] z-40 bg-[#F9FAF7] pt-2 pb-1 -mx-3 sm:-mx-6 lg:-mx-8 px-3 sm:px-6 lg:px-8 border-b border-[#E2E6D9]/60 shadow-[0_2px_6px_-4px_rgba(26,28,22,0.12)]" data-testid="quotations-filter-bar">
      {/* Search + Salesperson filter */}
      <div className="mb-3 sm:mb-4 flex flex-col sm:flex-row gap-2 sm:gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8F9485]" />
          <Input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search by customer name, phone, or quotation number..."
            className="pl-10 bg-white border-[#E2E6D9]"
            data-testid="quotations-search-input"
          />
          {search && search.trim() && (
            <p
              className="text-[10px] text-[#8F9485] italic mt-1 ml-1"
              data-testid="quotations-searching-hint"
            >
              Searching all quotations — date range temporarily ignored.
            </p>
          )}
        </div>
        {isPrivileged && (
          <div className="flex items-center gap-2 sm:w-64">
            <Filter className="w-4 h-4 text-[#8F9485] shrink-0" />
            <Select
              value={salespersonId || 'all'}
              onValueChange={(v) => { setSalespersonId(v === 'all' ? '' : v); setPage(1); }}
            >
              <SelectTrigger className="bg-white border-[#E2E6D9]" data-testid="salesperson-filter-select">
                <SelectValue placeholder="All salespeople" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">All salespeople</SelectItem>
                {salespeople.map(sp => (
                  <SelectItem key={sp.id} value={sp.id}>
                    {sp.name}
                    {sp.staff_code ? ` (${sp.staff_code})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {salespersonId && (
              <Button
                variant="ghost"
                size="sm"
                className="h-9 px-2 shrink-0"
                onClick={() => { setSalespersonId(''); setPage(1); }}
                title="Clear filter"
                data-testid="salesperson-filter-clear"
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        )}
      </div>
      <p className="text-xs text-[#8F9485] -mt-2 mb-3 ml-1">
        Tip: Search by customer name or phone, or pick a salesperson to see only their quotations.
      </p>

      {/* Date-range filter. Quick-pick chips + custom date
          inputs. Defaults to "Last 10 days" so the history view opens
          fast on-mobile. Picking a preset overwrites the dates; typing
          into either date input switches the preset to "custom".
          When the search box has text this row is visually dimmed
          and pointer-events disabled — search runs over ALL history
          so the range is temporarily meaningless. */}
      {(() => {
        const searching = !!(search && search.trim());
        return (
          <div
            className={`mb-4 bg-white border border-[#E2E6D9] rounded-lg p-2.5 sm:p-3 transition-opacity ${searching ? 'opacity-40 pointer-events-none select-none' : ''}`}
            aria-disabled={searching}
            data-testid="date-range-block"
          >
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-[#5C6150] font-semibold uppercase tracking-wide mr-1">Date range:</span>
              {[
                { key: '10d',   label: 'Last 10 days' },
                { key: '30d',   label: 'Last 30 days' },
                { key: 'month', label: 'This month' },
                { key: 'all',   label: 'All time' },
              ].map(p => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => applyPreset(p.key)}
                  disabled={searching}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                    rangePreset === p.key
                      ? 'bg-[#556B2F] border-[#556B2F] text-white'
                      : 'bg-white border-[#E2E6D9] text-[#5C6150] hover:border-[#556B2F]'
                  }`}
                  data-testid={`date-range-preset-${p.key}`}
                >
                  {p.label}
                </button>
              ))}
              <div className="flex items-center gap-1.5 ml-auto">
                <Input
                  type="date"
                  value={startDate}
                  onChange={e => { setStartDate(e.target.value); setRangePreset('custom'); setPage(1); }}
                  disabled={searching}
                  className="h-8 text-xs w-[130px] font-mono"
                  data-testid="date-range-start"
                />
                <span className="text-[#8F9485] text-xs">to</span>
                <Input
                  type="date"
                  value={endDate}
                  onChange={e => { setEndDate(e.target.value); setRangePreset('custom'); setPage(1); }}
                  disabled={searching}
                  className="h-8 text-xs w-[130px] font-mono"
                  data-testid="date-range-end"
                />
                {rangePreset === 'custom' && (
                  <span className="text-[10px] text-[#DAA520] font-semibold uppercase tracking-wide">Custom</span>
                )}
              </div>
            </div>
          </div>
        );
      })()}
      </div>{/* /sticky filter block */}

      {/* Stale summary banner — top-of-page nudge that shows the total
          number of pending quotes 30+ days old across ALL history (not
          just the current date-range view). Clicking "View them" flips
          into the stale-only view so the badges become visible. Hidden
          once we're already in stale-only mode to avoid double-banner. */}
      {!staleMode && staleSummary.count > 0 && (
        <div
          className="mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs sm:text-sm text-amber-900 flex items-center justify-between gap-3"
          data-testid="stale-summary-banner"
        >
          <span>
            <span className="font-semibold">{staleSummary.count}</span> pending quotation{staleSummary.count === 1 ? '' : 's'} older than 30 days
            {staleSummary.oldest_days > 30 ? (
              <span className="text-amber-700"> · oldest {staleSummary.oldest_days}d</span>
            ) : null}
          </span>
          <button
            type="button"
            onClick={enterStaleView}
            className="text-xs font-semibold underline underline-offset-2 hover:text-amber-950 shrink-0"
            data-testid="stale-summary-view-btn"
          >
            View them
          </button>
        </div>
      )}

      {/* Stale-only banner — shown when the user has clicked a
          Pending Nd badge. Dismiss ("Show all") restores whatever
          date range / preset was active before. */}
      {staleMode && (
        <div
          className="mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex items-center justify-between gap-3"
          data-testid="stale-only-banner"
        >
          <span>Showing quotations pending 30+ days.</span>
          <button
            type="button"
            onClick={exitStaleView}
            className="text-xs font-semibold underline underline-offset-2 hover:text-amber-950"
            data-testid="stale-only-show-all"
          >
            Show all
          </button>
        </div>
      )}

      {/* Quotations List */}
      <div className="space-y-2">
        {loading ? (
          <p className="text-center py-12 text-[#8F9485]">Loading...</p>
        ) : displayedQuotations.length === 0 ? (
          <Card className="border-[#E2E6D9]">
            <CardContent className="text-center py-12 text-[#8F9485]">
              {staleMode ? 'No pending quotations older than 30 days.' : 'No quotations found'}
            </CardContent>
          </Card>
        ) : (
          displayedQuotations.map(q => {
            // Comparison quotations get a visually distinct
            // light-yellow tint + "COMPARISON" tag so the two option
            // rows are obvious as a pair. They're still separate rows
            // (user wants to see each option's total), but the shared
            // yellow band pulls the eye to them as a group. Option 1 =
            // parent (no comparison_parent_id); Option 2 = child.
            const isComparison = !!q.is_comparison;
            const cmpLabel = q.comparison_label
              || (q.comparison_parent_id ? 'Option 2' : (isComparison ? 'Option 1' : ''));
            return (
            <Card
              key={q.id}
              className={
                `hover:shadow-sm transition-shadow ${
                  isComparison
                    ? 'border-[#DAA520]/60 bg-[#FDF6E1]/50 border-l-4 border-l-[#DAA520]'
                    : 'border-[#E2E6D9]'
                }`
              }
              data-testid={`quotation-${q.quotation_number}`}
            >
              <CardContent className="py-2 px-3 sm:px-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-1.5 sm:gap-3 mb-1">
                      <span className="font-semibold font-mono-data text-xs sm:text-sm text-[#556B2F]">{q.quotation_number}</span>
                      <span className="text-[10px] sm:text-xs text-[#8F9485]">{formatDate(q.created_at)}</span>
                      {/* Stale-pending badge. Only rendered for pending
                          quotations 30+ days old (per feature spec).
                          Click toggles the panel-wide stale-only view. */}
                      {(() => {
                        const st = (q.order_status || 'pending').toLowerCase();
                        if (st !== 'pending') return null;
                        const d = daysPending(q.created_at);
                        if (d < 30) return null;
                        const cls = d >= 60
                          ? 'bg-red-100 text-red-800 border-red-300'
                          : 'bg-amber-100 text-amber-800 border-amber-300';
                        return (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); enterStaleView(); }}
                            className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wide ${cls}`}
                            title="Show all stale pending quotations"
                            data-testid={`stale-badge-${q.id}`}
                          >
                            Pending {d}d
                          </button>
                        );
                      })()}
                      {isComparison && (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#DAA520] text-white text-[10px] font-bold uppercase tracking-wide"
                          data-testid={`comparison-badge-${q.id}`}
                        >
                          ⇔ {cmpLabel}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-[#1A1C16] font-medium truncate">{q.customer?.name}</p>
                    {q.customer?.mobile && <p className="text-[10px] sm:text-xs text-[#8F9485]">{q.customer.mobile}</p>}
                    {q.creator_name && q.creator_user_id && q.creator_user_id !== q.created_by && (
                      <p
                        className="text-[10px] sm:text-xs text-[#556B2F] italic mt-1"
                        data-testid={`creator-on-behalf-${q.id}`}
                      >
                        ✍ Made on your behalf by <span className="font-semibold">{q.creator_name}</span>
                      </p>
                    )}
                    {q.updated_by_name && q.updated_by && q.updated_by !== q.created_by && (
                      <p
                        className="text-[10px] sm:text-xs text-[#B07700] italic mt-1"
                        data-testid={`edited-by-${q.id}`}
                      >
                        ✎ Last edited by{' '}
                        <span className="font-semibold">{q.updated_by_name}</span>
                        {q.updated_by_role ? <span className="capitalize"> ({q.updated_by_role})</span> : null}
                        {q.updated_at ? <span className="ml-1">• {formatDate(q.updated_at)}</span> : null}
                      </p>
                    )}
                    {/* Duplicate badges — `merged-into` for older siblings folded
                        into a newer winner; `absorbs` for the winner showing how
                        many older copies it represents. Both link to the kept row
                        so the user can compare. */}
                    {duplicateMap[q.id] && (
                      <p
                        className="text-[10px] sm:text-xs text-indigo-700 italic mt-1 inline-flex items-center gap-2"
                        data-testid={`merged-into-${q.id}`}
                      >
                        <span className="px-1.5 py-0.5 rounded bg-indigo-50 border border-indigo-200">
                          🔗 Merged into latest version
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate(`/quotations/${duplicateMap[q.id]}`); }}
                            className="ml-1 underline hover:text-indigo-900"
                          >
                            view latest
                          </button>
                        </span>
                        {isPrivileged && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleUnmerge(q); }}
                            className="text-[#8B6914] underline hover:text-[#5A4408]"
                            data-testid={`unmerge-${q.id}`}
                          >
                            unmerge (count separately)
                          </button>
                        )}
                      </p>
                    )}
                    {winnerToDupCount[q.id] > 0 && (
                      <p
                        className="text-[10px] sm:text-xs text-emerald-700 italic mt-1"
                        data-testid={`absorbs-${q.id}`}
                      >
                        <span className="px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-200">
                          🔗 Counts as 1 (absorbs {winnerToDupCount[q.id]} earlier version{winnerToDupCount[q.id] > 1 ? 's' : ''})
                        </span>
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col sm:flex-row items-end sm:items-center gap-2 sm:gap-3">
                    <div className="text-right">
                      <p className="font-bold font-mono-data text-sm sm:text-base text-[#1A1C16]">{formatINR(q.grand_total)}</p>
                      <p className="text-[10px] sm:text-xs text-[#8F9485]">{q.items?.length || 0} items</p>
                    </div>
                    <div className="flex gap-0.5 sm:gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-11 w-11 sm:h-8 sm:w-8 p-0 text-[#556B2F] hover:bg-[#556B2F]/10"
                        onClick={() => navigate(`/quotations/${q.id}`)}
                        data-testid={`view-quotation-${q.id}`}
                      >
                        <Eye className="w-4 h-4 sm:w-4 sm:h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-11 w-11 sm:h-8 sm:w-8 p-0 text-[#DAA520] hover:bg-[#DAA520]/10"
                        onClick={() => handleDownload(q)}
                        data-testid={`download-quotation-${q.id}`}
                      >
                        <Download className="w-4 h-4 sm:w-4 sm:h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-11 w-11 sm:h-8 sm:w-8 p-0 text-red-500 hover:bg-red-500/10"
                        onClick={() => handleDeleteClick(q)}
                        data-testid={`delete-quotation-${q.id}`}
                      >
                        <Trash2 className="w-4 h-4 sm:w-4 sm:h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
                {/* Status control — compact segmented (Closed | Pending | Lost).
                    Sits directly below the customer / amount cluster with a
                    small top margin; no separator line to keep cards dense.
                    Clicking Lost still opens the required-reason modal. */}
                <div className="mt-2">
                  <OrderStatusButtons
                    quotationId={q.id}
                    status={q.order_status}
                    lostReason={q.lost_reason}
                    onStatusChange={(s, r) => api.updateQuotationStatus(q.id, { status: s, lost_reason: r })}
                    onChange={(newStatus, newReason) => {
                      setQuotations(prev => prev.map(x =>
                        x.id === q.id ? { ...x, order_status: newStatus, lost_reason: newReason || '' } : x
                      ));
                    }}
                  />
                </div>
              </CardContent>
            </Card>
          );
          })
        )}
      </div>

      {pages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-[#5C6150]">Page {page} of {pages}</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Quotation</AlertDialogTitle>
            <AlertDialogDescription>
              Delete quotation <strong>{quotationToDelete?.quotation_number}</strong>? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setQuotationToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} className="bg-red-500 hover:bg-red-600">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ReviewPossibleDuplicatesDialog
        open={reviewDupsOpen}
        onOpenChange={setReviewDupsOpen}
      />
    </div>
  );
}
